import React, { useState } from 'react';
import { User } from 'lucide-react';

const SIZE_MAP = {
  xs: { size: 'h-5 w-5', icon: 'h-3 w-3', text: 'text-xs' },
  sm: { size: 'h-6 w-6', icon: 'h-3 w-3', text: 'text-xs' },
  md: { size: 'h-8 w-8', icon: 'h-4 w-4', text: 'text-sm' },
  lg: { size: 'h-10 w-10', icon: 'h-6 w-6', text: 'text-base' },
  xl: { size: 'h-12 w-12', icon: 'h-7 w-7', text: 'text-lg' }
};

const resolveAvatarUrl = (avatar, authServiceUrl, useAuthServiceUrl) => {
  if (!avatar || typeof avatar !== 'string') return '';
  if (avatar.startsWith('http') || avatar.startsWith('data:')) return avatar;
  if (!useAuthServiceUrl) return avatar;
  return `${authServiceUrl || ''}${avatar}`;
};

const Avatar = ({
  avatar,
  name,
  email,
  alt,
  size = 'md',
  sizeClasses,
  iconSizeClasses,
  textSizeClasses,
  className = '',
  imageClassName = '',
  fallbackType = 'auto',
  showInitialOnDefault = true,
  showInitialOnMissing = false,
  defaultAvatarPath = '/uploads/avatars/default.jpg',
  fallbackBgClassName = 'bg-primary-100 dark:bg-primary-900/30',
  initialBgClassName,
  iconBgClassName,
  initialTextClassName = 'text-primary-600 dark:text-primary-400',
  iconClassName = 'text-primary-600 dark:text-primary-400',
  fallbackClassName = '',
  icon: Icon = User,
  useAuthServiceUrl = true,
  authServiceUrl = import.meta.env.VITE_AUTH_SERVICE_URL || '',
  crossOrigin = 'anonymous',
  referrerPolicy = 'no-referrer'
}) => {
  const [hasError, setHasError] = useState(false);

  const sizeConfig = SIZE_MAP[size] || {};
  const resolvedSizeClasses = sizeClasses || sizeConfig.size || size || 'h-8 w-8';
  const resolvedIconSizeClasses = iconSizeClasses || sizeConfig.icon || 'h-4 w-4';
  const resolvedTextSizeClasses = textSizeClasses || sizeConfig.text || 'text-sm';

  const displayName = name || email || '';
  const firstLetter = displayName.trim().charAt(0).toUpperCase();
  const isDefaultAvatar = avatar === defaultAvatarPath;
  const canShowImage = avatar && !isDefaultAvatar && !hasError;
  const shouldShowInitial = Boolean(firstLetter) && (
    fallbackType === 'initial' ||
    (fallbackType === 'auto' && (
      (isDefaultAvatar && showInitialOnDefault) ||
      (!avatar && showInitialOnMissing) ||
      hasError
    ))
  );

  if (canShowImage) {
    const resolvedAlt = alt || displayName || 'User';
    const avatarUrl = resolveAvatarUrl(avatar, authServiceUrl, useAuthServiceUrl);
    return (
      <img
        src={avatarUrl}
        alt={resolvedAlt}
        className={`${resolvedSizeClasses} rounded-full object-cover ${className} ${imageClassName}`.trim()}
        crossOrigin={crossOrigin}
        referrerPolicy={referrerPolicy}
        onError={() => setHasError(true)}
      />
    );
  }

  if (shouldShowInitial) {
    const resolvedInitialBg = initialBgClassName || fallbackBgClassName;
    return (
      <div
        className={`${resolvedSizeClasses} rounded-full flex items-center justify-center ${resolvedInitialBg} ${fallbackClassName} ${className}`.trim()}
      >
        <span className={`${resolvedTextSizeClasses} font-semibold ${initialTextClassName}`.trim()}>
          {firstLetter}
        </span>
      </div>
    );
  }

  const resolvedIconBg = iconBgClassName || fallbackBgClassName;
  return (
    <div
      className={`${resolvedSizeClasses} rounded-full flex items-center justify-center ${resolvedIconBg} ${fallbackClassName} ${className}`.trim()}
    >
      <Icon className={`${resolvedIconSizeClasses} ${iconClassName}`.trim()} />
    </div>
  );
};

export default Avatar;
